// THE PRACTICE GAME (Matt, after playtest 9: "turn on a tutorial and then it
// tells you up front, this is not affecting the game. We're just going to
// walk you through the steps"). A fresh game, dealt from a fixed seed, on
// this phone only: the real room keeps going underneath and the table never
// sees a thing. The same seed every time, so every player meets the same
// first day and the scripts can count on what is on the board.

import {
  createGameState, dispatch, allocateAction, startDayAction,
} from '@/engine/game';
import { ROLES } from '@/engine/constants';
import { cityOf } from '@/engine/city';
import { TECHS } from '@/engine/cards';
import { natureKey, NATURES } from '@/engine/lore';

export const TUTORIAL_SEED = 90210;
// Who holds the other three roles: nobody on this phone, so the board shows
// only the one being learned.
export const TUTOR_ID = 'tutor';

// What each role's first day needs on the table to be worth teaching.
const STAGE = {
  // Enough in the stores that Build has something to build.
  trade: (civ) => ({ ...civ, goods: 6, coin: 4, food: 6 }),
  // A pioneer beside the warband, so "units are different" can be shown.
  military: (civ) => ({
    ...civ,
    units: [...civ.units, { id: 'u2', kind: 'pioneer', q: 0, r: 0, strength: 1 }],
    unitSeq: Math.max(civ.unitSeq || 1, 2),
    coin: 4,
  }),
  culture: (civ) => ({ ...civ, goods: 4, coin: 4 }),
  science: (civ) => ({ ...civ, knowledge: 2, goods: Math.max(civ.goods || 0, 4) }),
  // THE MACHINES: a Kiln standing, the tree up to Iron's door, goods for a
  // few firings, and one glaze on the shelf that is exactly what Iron needs.
  machines: (civ) => {
    const city = cityOf(civ);
    const need = civ.lore?.reactions?.iron || {};
    const fit = NATURES.find((n) => Object.entries(need).every(([k, v]) => n[k] === v)) || NATURES[0];
    return {
      ...civ,
      knowledge: Math.max(civ.knowledge || 0, TECHS.iron.cost + 2),
      goods: 10,
      techs: [...new Set([...(civ.techs || []), 'pottery', 'kilns', 'tracking', 'bronze'])],
      city: { ...city, seq: (city.seq || 0) + 1, buildings: [...city.buildings, { id: `b${(city.seq || 0) + 1}`, type: 'kiln', hex: [1, -1], staffed: 0, burned: false }] },
      lore: { ...civ.lore, shelf: { kiln: { [natureKey(fit)]: 1 } } },
    };
  },
};
// A tutorial that is not a role's own (the machines) says which role it is held as.
export const ROLE_OF = { machines: 'science' };
export const roleOfTutorial = (key) => ROLE_OF[key] || key;

/**
 * The practice game for `role`, at DAWN: every worker but one already given
 * to that role, the last one for the player to give out themselves (Matt,
 * 2026-10-12: "clearly explain how the dawn works and how workers are
 * distributed" - so the tutorial has you do one). The role is held by
 * `playerId`, the other three by nobody here.
 */
export const makeTutorialGame = (key, playerId, name = 'You') => {
  const role = roleOfTutorial(key);
  let g = createGameState({ players: [{ id: playerId, name }], seed: TUTORIAL_SEED });
  g = { ...g, civ: (STAGE[key] || ((c) => c))(g.civ) };
  ROLES.forEach((r) => {
    const n = g.allocation?.[r] || 0;
    if (n) g = dispatch(g, allocateAction(r, -n));
  });
  g = dispatch(g, allocateAction(role, g.civ.workers - 1));
  const roles = Object.fromEntries(ROLES.map((r) => [r, r === role ? playerId : TUTOR_ID]));
  // The machines start mid-morning: their lesson is not the dawn.
  if (key !== role) g = startPracticeDay(g, role);
  return { ...g, roles, tutorial: role };
};

/** The practice dawn done the way the tutorial asks (tests, and a skip). */
export const startPracticeDay = (g, role) => {
  let next = dispatch(g, allocateAction(role, g.civ.workers - (g.allocation?.[role] || 0)));
  next = dispatch(next, startDayAction());
  return next;
};

// What a practice day leaves behind on this phone (the Scholars' sheet is
// kept per seed), cleared so a second run starts clean.
export const clearTutorialTraces = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.includes(`:${TUTORIAL_SEED}`))
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* storage off */ }
};
